/**
 * Pestaña Clips — el clip end-to-end de la demo.
 *
 *   *"Filtrá los ataques del 14 en rotación 1… ahí están los seis videos,
 *     desde las cuatro cámaras."*
 *
 * Tres piezas que ya existían y que hasta esta ola no hablaban entre sí:
 *
 *  1. **el log sabe QUÉ cortar** — `GET /api/matches/:id/clips` devuelve los
 *     cortes que matchean el filtro (equipo, camiseta, fundamento, evaluación,
 *     set y ROTACIÓN), cada uno con su `videoNs` y su pre/post roll;
 *  2. **el VAR de la cancha CORTA** — `exportClip` con la ventana y las cámaras
 *     elegidas, y `clipStatus` para saber cuándo está;
 *  3. **el navegador BAJA el archivo del VAR**, directo por la LAN.
 *
 * TOPOLOGÍA (ADR-017): el paso 2 y el 3 NO pasan por el API. Esta pantalla le
 * habla al server de replay de la cancha con la URL y el token que están en
 * Ajustes, igual que hace la captura. Por eso el estado de cada export vive
 * acá, en el navegador: no hay a quién preguntarle desde otro lado.
 *
 * SIN VAR CONFIGURADO LA PANTALLA SIRVE IGUAL: la lista de cortes sale del log
 * —con etiqueta, timecode y duración— y se puede leer, filtrar y dictar. Lo
 * único que falta es el botón que produce el archivo, y se dice por qué.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReplayCamera, ReplayClipPort } from '@scout/core';
import { createReplayClient } from '@scout/replay-adapter';
import { Card, Empty, ErrorBox, Loading, Notice } from '../../components/States';
import { errorMessage, useAsync } from '../../hooks';
import { useT } from '../../i18n/use-t';
import type { ApiClient, Clip, ClipFilterQuery } from '../../lib/api-client';
import type { PreviousRallyFilter } from '../../lib/rally-filter';
import {
  CLIP_EVALUATIONS,
  CLIP_SKILLS,
  ROTATIONS,
  clipDurationSeconds,
  clipWindow,
  failJob,
  filesToExport,
  formatTimecode,
  patchJob,
  pollableJobs,
  startJob,
  summarizeJobs,
  type ClipJobs,
} from '../../lib/clips';

/** Cada cuánto se le pregunta al VAR por un job en curso. */
const POLL_MS = 1500;

type Side = 'home' | 'away';

export function ClipsTab({
  client,
  matchId,
  sets,
  previousRally,
  homePlayers,
  awayPlayers,
  homeName,
  awayName,
  replayUrl,
  replayToken,
}: {
  client: ApiClient;
  matchId: string;
  /** Recorte de sets de la página (el selector de arriba, común a todas las pestañas). */
  sets: number[] | undefined;
  /**
   * "One Rally Before" de la página (mismo selector de arriba): el momento
   * fuerte de la feature — *"los clips de los sideouts que vinieron después de
   * perder el break"* es este filtro cruzado con los de acá abajo.
   */
  previousRally: PreviousRallyFilter | undefined;
  homePlayers: readonly number[];
  awayPlayers: readonly number[];
  homeName: string;
  awayName: string;
  replayUrl: string;
  replayToken: string;
}): JSX.Element {
  const t = useT();
  const [team, setTeam] = useState<Side | ''>('');
  const [player, setPlayer] = useState<number | null>(null);
  const [skill, setSkill] = useState<string>('attack');
  const [evaluation, setEvaluation] = useState<string>('');
  const [rotation, setRotation] = useState<number | null>(null);
  const [selectedCameras, setSelectedCameras] = useState<string[] | null>(null);
  const [jobs, setJobs] = useState<ClipJobs>({});

  /**
   * Cliente del VAR. Se instancia con la config de ESTE navegador; `null` = no
   * hay VAR configurado, que es un estado normal (un analista mirando desde la
   * federación no tiene la LAN de la cancha).
   */
  const port: ReplayClipPort | null = useMemo(
    () => (replayUrl.trim() === '' ? null : createReplayClient({ baseUrl: replayUrl, token: replayToken })),
    [replayUrl, replayToken],
  );

  const filter: ClipFilterQuery = useMemo(
    () => ({
      ...(team === '' ? {} : { team }),
      ...(player === null ? {} : { player }),
      ...(skill === '' ? {} : { skill }),
      ...(evaluation === '' ? {} : { evaluation }),
      ...(sets === undefined ? {} : { sets }),
      ...(previousRally === undefined ? {} : { previousRally }),
      // La rotación es de un equipo: sin equipo elegido se pregunta por el
      // local, que es el default de toda la pantalla del partido.
      ...(rotation === null ? {} : { rotation: [rotation], rotationTeam: team === '' ? 'home' : team }),
    }),
    [team, player, skill, evaluation, sets, rotation, previousRally],
  );

  const playlist = useAsync(
    () => client.getClipPlaylist(matchId, filter),
    [client, matchId, team, player, skill, evaluation, rotation, sets?.join(','), previousRally],
  );

  const cameras = useAsync<ReplayCamera[]>(
    () => (port ? port.listCameras() : Promise.resolve([])),
    [port],
  );

  const cameraIds = useMemo(() => (cameras.data ?? []).map((c) => c.id), [cameras.data]);
  // Sin elección explícita se piden TODAS las cámaras: es lo que hace útil al
  // clip (la falta se ve desde una y no desde otra) y es la frase de la demo.
  const chosen = selectedCameras ?? cameraIds;

  const toggleCamera = (id: string): void => {
    const current = new Set(chosen);
    if (current.has(id)) current.delete(id);
    else current.add(id);
    setSelectedCameras(cameraIds.filter((camera) => current.has(camera)));
  };

  const clips = playlist.data?.data.clips ?? [];
  const skipped = playlist.data?.data.clipsSkipped ?? 0;
  const summary = summarizeJobs(jobs);

  /** Pide un corte al VAR y deja el job en seguimiento. */
  const requestClip = useCallback(
    async (clip: Clip): Promise<void> => {
      if (!port) return;
      setJobs((prev) => startJob(prev, clip.eventId, chosen));
      try {
        const window = clipWindow(clip);
        const { jobId } = await port.exportClip({
          inNs: window.inNs,
          outNs: window.outNs,
          cameras: chosen.length > 0 ? chosen : undefined,
          label: clip.label,
        });
        setJobs((prev) => patchJob(prev, clip.eventId, { jobId, state: 'queued' }));
      } catch (err) {
        setJobs((prev) => failJob(prev, clip.eventId, err instanceof Error ? err.message : String(err)));
      }
    },
    [port, chosen],
  );

  /**
   * El botón de la demo: los N clips de la lista, de una.
   *
   * Los pedidos salen EN ORDEN, no en paralelo: son N × cámaras archivos para
   * un binario que está grabando en vivo, y dispararle veinte requests juntas
   * el día de la reunión es la clase de cosa que lo pone lento justo ahí. El
   * encolado real lo hace el VAR; nosotros sólo no lo empujamos.
   */
  const requestAll = useCallback(async (): Promise<void> => {
    for (const clip of clips) await requestClip(clip);
  }, [clips, requestClip]);

  /**
   * Polling de los jobs vivos. Se reprograma con cada cambio de `jobs`, así que
   * termina solo cuando no queda ninguno en curso: no hay intervalo colgado.
   */
  useEffect(() => {
    if (!port) return;
    const pending = pollableJobs(jobs);
    if (pending.length === 0) return;
    let alive = true;
    const timer = setTimeout(() => {
      void Promise.all(
        pending.map(async (job) => {
          try {
            const status = await port.clipStatus(job.jobId as string);
            if (!alive) return;
            setJobs((prev) =>
              patchJob(prev, job.eventId, {
                state: status.state,
                url: status.url,
                progress: status.progress,
                error: status.error,
              }),
            );
          } catch (err) {
            if (!alive) return;
            setJobs((prev) =>
              failJob(prev, job.eventId, err instanceof Error ? err.message : String(err)),
            );
          }
        }),
      );
    }, POLL_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [jobs, port]);

  const players = team === 'away' ? awayPlayers : team === 'home' ? homePlayers : [];

  return (
    <div>
      <Card title={t('clips.filterCard')}>
        <p className="hint">{t('clips.intro')}</p>
        <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
          <label className="row" style={{ gap: 6 }}>
            <span className="hint">{t('clips.team')}</span>
            <select
              value={team}
              onChange={(e) => {
                setTeam(e.target.value === '' ? '' : (e.target.value as Side));
                // La camiseta es de un equipo: cambiar de lado la limpia, si no
                // la lista queda vacía sin que se entienda por qué.
                setPlayer(null);
              }}
              style={{ width: 'auto' }}
            >
              <option value="">{t('clips.bothTeams')}</option>
              <option value="home">{homeName}</option>
              <option value="away">{awayName}</option>
            </select>
          </label>

          <label className="row" style={{ gap: 6 }}>
            <span className="hint">{t('clips.player')}</span>
            <select
              value={player === null ? '' : String(player)}
              onChange={(e) => setPlayer(e.target.value === '' ? null : Number(e.target.value))}
              style={{ width: 'auto' }}
              disabled={team === ''}
              title={team === '' ? t('clips.playerNeedsTeam') : undefined}
            >
              <option value="">{t('clips.allPlayers')}</option>
              {players.map((number) => (
                <option key={number} value={number}>
                  #{number}
                </option>
              ))}
            </select>
          </label>

          <label className="row" style={{ gap: 6 }}>
            <span className="hint">{t('clips.skill')}</span>
            <select value={skill} onChange={(e) => setSkill(e.target.value)} style={{ width: 'auto' }}>
              <option value="">{t('clips.allSkills')}</option>
              {CLIP_SKILLS.map((id) => (
                <option key={id} value={id}>
                  {t(`clips.skill_${id}` as 'clips.skill_attack')}
                </option>
              ))}
            </select>
          </label>

          <label className="row" style={{ gap: 6 }}>
            <span className="hint">{t('clips.evaluation')}</span>
            <select
              value={evaluation}
              onChange={(e) => setEvaluation(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="">{t('clips.allEvaluations')}</option>
              {CLIP_EVALUATIONS.map((grade) => (
                <option key={grade} value={grade}>
                  {grade}
                </option>
              ))}
            </select>
          </label>

          <label className="row" style={{ gap: 6 }}>
            <span className="hint">{t('clips.rotation')}</span>
            <select
              value={rotation === null ? '' : String(rotation)}
              onChange={(e) => setRotation(e.target.value === '' ? null : Number(e.target.value))}
              style={{ width: 'auto' }}
            >
              <option value="">{t('clips.allRotations')}</option>
              {ROTATIONS.map((position) => (
                <option key={position} value={position}>
                  {t('clips.rotationOption', { position })}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="hint" style={{ marginTop: 8 }}>
          {t('clips.setsHint')}
        </p>
      </Card>

      {port === null ? (
        <Notice message={t('clips.noVar')} hint={t('clips.noVarHint')} />
      ) : cameras.status === 'error' ? (
        <ErrorBox message={t('clips.camerasError', { reason: errorMessage(cameras.error) })} onRetry={cameras.reload} />
      ) : (
        <Card title={t('clips.camerasCard')}>
          {cameras.data === null ? (
            <Loading what={t('clips.camerasWhat')} />
          ) : cameras.data.length === 0 ? (
            <Empty>{t('clips.noCameras')}</Empty>
          ) : (
            <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
              {cameras.data.map((camera) => (
                <label key={camera.id} className="row" style={{ gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={chosen.includes(camera.id)}
                    onChange={() => toggleCamera(camera.id)}
                  />
                  <span>
                    {camera.label} <span className="hint">({camera.group} · {camera.fps} fps)</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </Card>
      )}

      <Card title={t('clips.listCard')}>
        {playlist.status === 'error' ? (
          <ErrorBox message={errorMessage(playlist.error)} onRetry={playlist.reload} />
        ) : null}

        {playlist.data === null && playlist.status === 'loading' ? (
          <Loading what={t('clips.loadingWhat')} />
        ) : null}

        {playlist.data !== null ? (
          <>
            <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <b>{t('clips.count', { count: clips.length })}</b>
              {skipped > 0 ? <span className="hint">{t('clips.skipped', { count: skipped })}</span> : null}
              {port !== null && clips.length > 0 ? (
                <>
                  <span className="hint">
                    {t('clips.files', {
                      files: filesToExport(clips.length, chosen.length),
                      cameras: chosen.length,
                    })}
                  </span>
                  <button type="button" className="primary" onClick={() => void requestAll()}>
                    {t('clips.requestAll', { count: clips.length })}
                  </button>
                </>
              ) : null}
              {summary.total > 0 ? (
                <span className="chip ok">
                  {t('clips.jobsSummary', {
                    done: summary.done,
                    working: summary.working,
                    failed: summary.failed,
                  })}
                </span>
              ) : null}
            </div>

            <ClipsTable
              clips={clips}
              jobs={jobs}
              canRequest={port !== null}
              onRequest={(clip) => void requestClip(clip)}
            />
          </>
        ) : null}
      </Card>
    </div>
  );
}

/**
 * La tabla de cortes. Está separada del resto de la pestaña a propósito: es la
 * parte que se ve en la demo y la única que se puede renderizar en un test sin
 * DOM ni red (el resto es ciclo de vida: pedir la playlist, pollear los jobs).
 */
export function ClipsTable({
  clips,
  jobs,
  canRequest,
  onRequest,
}: {
  clips: readonly Clip[];
  jobs: ClipJobs;
  /** Hay VAR configurado: sin él la lista se lee igual pero no se pide nada. */
  canRequest: boolean;
  onRequest: (clip: Clip) => void;
}): JSX.Element {
  const t = useT();
  if (clips.length === 0) return <Empty>{t('clips.emptyForFilter')}</Empty>;
  return (
    <table>
      <thead>
        <tr>
          <th>{t('clips.colClip')}</th>
          <th>{t('clips.colSet')}</th>
          <th>{t('clips.colTimecode')}</th>
          <th>{t('clips.colDuration')}</th>
          <th>{t('clips.colState')}</th>
        </tr>
      </thead>
      <tbody>
        {clips.map((clip) => {
          const job = jobs[clip.eventId];
          return (
            <tr key={clip.eventId}>
              <td>{clip.label}</td>
              <td>{clip.set}</td>
              <td title={t('clips.timecodeTitle', { ns: clip.videoNs })}>
                {formatTimecode(clip.videoNs)}
              </td>
              <td>{t('clips.seconds', { seconds: clipDurationSeconds(clip) })}</td>
              <td>
                {job === undefined ? (
                  <button
                    type="button"
                    className="small"
                    disabled={!canRequest}
                    title={canRequest ? undefined : t('clips.noVar')}
                    onClick={() => onRequest(clip)}
                  >
                    {t('clips.request')}
                  </button>
                ) : job.state === 'done' && job.url !== undefined ? (
                  // El archivo lo sirve el VAR de la cancha, no el portal: por
                  // eso es un link a otra máquina y no una descarga nuestra.
                  <a href={job.url} target="_blank" rel="noreferrer">
                    {t('clips.download')}
                  </a>
                ) : job.state === 'error' ? (
                  <span className="chip bad" title={job.error}>
                    {t('clips.failed')}
                  </span>
                ) : (
                  <span className="hint">
                    {t(`clips.state_${job.state}` as 'clips.state_queued')}
                    {job.progress !== undefined ? ` ${Math.round(job.progress * 100)}%` : ''}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
